import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { api, ApiError } from "../lib/api";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Wenn true: User ohne Profile wird zu /onboarding/fotos geschickt. Default true. */
  requireProfile?: boolean;
  /** Wenn true: User muss aus der Warteliste raus sein. Default false. */
  requireNoWaitlist?: boolean;
}

/**
 * Prüft Session; zusätzlich (falls requireProfile) ob bereits ein Profil
 * existiert — ansonsten zum Onboarding. So landet niemand mit User aber
 * ohne Profil im NotFound-Limbo.
 */
export function ProtectedRoute({
  children,
  requireProfile = true,
  requireNoWaitlist = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  const profileQuery = useQuery({
    queryKey: ["profile", "me"],
    queryFn: () =>
      api.get<{
        id: string;
        onWaitlist: boolean;
      }>("/api/profile/me"),
    enabled: isAuthenticated && requireProfile,
    retry: (count, err) =>
      err instanceof ApiError && err.status === 404 ? false : count < 1,
  });

  if (isLoading || (requireProfile && profileQuery.isPending && isAuthenticated)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${redirect}`} replace />;
  }

  if (requireProfile) {
    const err = profileQuery.error;
    const noProfile = err instanceof ApiError && err.status === 404;
    if (noProfile && location.pathname !== "/onboarding/fotos") {
      return <Navigate to="/onboarding/fotos" replace />;
    }
    if (
      requireNoWaitlist &&
      profileQuery.data?.onWaitlist &&
      location.pathname !== "/warteliste"
    ) {
      return <Navigate to="/warteliste" replace />;
    }
  }

  return <>{children}</>;
}
